import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCw, Maximize2, Minimize2, Columns } from 'lucide-react';

const InbuiltPDFViewer = ({ url, title, onClose }) => {
  const [pdfjsLib, setPdfjsLib] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);
  // Default scale based on screen size
  const [scale, setScale] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      return 1.0; // Smaller scale for mobile
    }
    return 1.5; // Default scale for desktop
  });

  // Detect mobile screen size for responsive scale
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768;
    }
    return false;
  });
  
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [renderedPages, setRenderedPages] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDoubleSide, setIsDoubleSide] = useState(false);
  const canvasRefs = useRef({});
  const renderTasksRef = useRef({});
  const containerRef = useRef(null);
  const viewerRef = useRef(null);

  // Load PDF.js library
  useEffect(() => {
    const loadPDFJS = async () => {
      try {
        const pdfjs = await import('pdfjs-dist');
        
        // Try to use local worker file first, fallback to CDN
        try {
          pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
        } catch {
          // Fallback to CDN if local file doesn't exist
          pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
        }
        
        setPdfjsLib(pdfjs);
      } catch (err) {
        setError('Failed to load PDF viewer library');
        setLoading(false);
      }
    };
    loadPDFJS();
  }, []);

  // Load PDF document
  useEffect(() => {
    if (!pdfjsLib || !url) return;

    const loadPDF = async () => {
      try {
        setLoading(true);
        setError(null);
        
        let arrayBuffer;
        let downloadUrl = url;
        const isFirebaseStorage = url.includes('firebasestorage.googleapis.com');
        
        if (isFirebaseStorage) {
          // For Firebase Storage, ALWAYS use Firebase SDK to get proper download URL
          // This avoids CORS issues
          try {
            const { ref, getDownloadURL } = await import('firebase/storage');
            const { storage } = await import('../firebase/config');
            
            // Extract path from Firebase Storage URL
            // URL format: https://firebasestorage.googleapis.com/v0/b/BUCKET/o/PATH?alt=media&token=TOKEN
            const urlObj = new URL(url);
            const pathMatch = urlObj.pathname.match(/\/o\/(.+)$/);
            
            if (pathMatch) {
              const filePath = decodeURIComponent(pathMatch[1]);
              
              const storageRef = ref(storage, filePath);
              downloadUrl = await getDownloadURL(storageRef);
            }
          } catch (firebaseError) {
            throw new Error(`Failed to get download URL from Firebase: ${firebaseError.message}. Please configure CORS or use Firebase SDK properly.`);
          }
        }
        
        // Try loading PDF directly from URL using PDF.js first (handles CORS better)
        // If that fails, fall back to fetching as blob
        let loadingTask;
        try {
          loadingTask = pdfjsLib.getDocument({
            url: downloadUrl,
            cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/cmaps/`,
            cMapPacked: true,
            httpHeaders: {
              'Accept': 'application/pdf',
            },
          });
          
          const pdf = await loadingTask.promise;
          setPdfDoc(pdf);
          setNumPages(pdf.numPages);
          setPageNum(1);
          setLoading(false);
          return; // Success, exit early
        } catch (urlError) {
          // Fallback: Fetch as blob and load from array buffer
          const response = await fetch(downloadUrl, {
            method: 'GET',
            headers: {
              'Accept': 'application/pdf',
            },
            // Don't include credentials to avoid CORS issues
            credentials: 'omit',
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const blob = await response.blob();
          arrayBuffer = await blob.arrayBuffer();

          // Load PDF from array buffer using PDF.js
          loadingTask = pdfjsLib.getDocument({
            data: arrayBuffer,
            cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/cmaps/`,
            cMapPacked: true,
          });
        }

        // Only reach here if we're using blob method
        const pdf = await loadingTask.promise;
        setPdfDoc(pdf);
        setNumPages(pdf.numPages);
        setPageNum(1);
        setLoading(false);
      } catch (err) {
        let errorMessage = `Failed to load PDF: ${err.message}`;
        
        if (err.message.includes('CORS') || err.message.includes('Failed to fetch') || err.message.includes('blocked')) {
          errorMessage += '. CORS policy is blocking the request. The Firebase Storage CORS may need to be configured.';
        } else if (err.message.includes('network')) {
          errorMessage += '. Network error occurred. Please check your connection.';
        } else if (err.message.includes('Firebase')) {
          errorMessage += '. Make sure you are authenticated and have permission to access this file.';
        }
        
        setError(errorMessage);
        setLoading(false);
      }
    };

    loadPDF();
  }, [pdfjsLib, url]);

  // Render all PDF pages in continuous layout
  useEffect(() => {
    if (!pdfDoc || numPages === 0) return;

    const renderAllPages = async () => {
      try {
        const pages = [];
        for (let i = 1; i <= numPages; i++) {
          const page = await pdfDoc.getPage(i);
          const viewport = page.getViewport({ 
            scale: scale,
            rotation: rotation
          });
          pages.push({ page, viewport, pageNum: i });
        }
        setRenderedPages(pages);
      } catch (err) {
        setError(`Failed to render pages: ${err.message}`);
      }
    };

    renderAllPages();
  }, [pdfDoc, numPages, scale, rotation]);

  // Render each page to its canvas
  useEffect(() => {
    if (renderedPages.length === 0) return;

    const renderAllCanvases = async () => {
      // Cancel any existing render tasks
      Object.values(renderTasksRef.current).forEach(task => {
        if (task && task.cancel) {
          try {
            task.cancel();
          } catch (e) {
            // Ignore cancellation errors
          }
        }
      });
      renderTasksRef.current = {};

      // Wait for DOM to update (especially important when switching between single/double-side modes)
      await new Promise(resolve => setTimeout(resolve, 50));

      // Render each page sequentially
      for (const { page, viewport, pageNum: pNum } of renderedPages) {
        const canvas = canvasRefs.current[pNum];
        if (!canvas) {
          // If canvas not found, wait a bit and try again
          await new Promise(resolve => setTimeout(resolve, 10));
          const retryCanvas = canvasRefs.current[pNum];
          if (!retryCanvas) {
            continue;
          }
          // Use retry canvas
          const context = retryCanvas.getContext('2d');
          retryCanvas.height = viewport.height;
          retryCanvas.width = viewport.width;
          context.clearRect(0, 0, retryCanvas.width, retryCanvas.height);
          
          const renderContext = {
            canvasContext: context,
            viewport: viewport
          };
          
          const renderTask = page.render(renderContext);
          renderTasksRef.current[pNum] = renderTask;
          await renderTask.promise;
          delete renderTasksRef.current[pNum];
          continue;
        }

        try {
          // Cancel previous render task for this canvas if exists
          if (renderTasksRef.current[pNum] && renderTasksRef.current[pNum].cancel) {
            try {
              renderTasksRef.current[pNum].cancel();
            } catch (e) {
              // Ignore cancellation errors
            }
          }

          // Set canvas dimensions first
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          
          const context = canvas.getContext('2d');
          
          // Clear canvas before rendering
          context.clearRect(0, 0, canvas.width, canvas.height);

          const renderContext = {
            canvasContext: context,
            viewport: viewport
          };

          // Start render and store the task
          const renderTask = page.render(renderContext);
          renderTasksRef.current[pNum] = renderTask;

          // Wait for render to complete
          await renderTask.promise;
          
          // Clear the task reference after completion
          delete renderTasksRef.current[pNum];
        } catch (err) {
          // Only handle if it's not a cancellation error
          if (!err.message || !err.message.includes('cancelled')) {
            // Silently handle rendering errors
          }
          delete renderTasksRef.current[pNum];
        }
      }
    };

    renderAllCanvases();

    // Cleanup: cancel all render tasks on unmount or when dependencies change
    return () => {
      Object.values(renderTasksRef.current).forEach(task => {
        if (task && task.cancel) {
          try {
            task.cancel();
          } catch (e) {
            // Ignore cancellation errors
          }
        }
      });
      renderTasksRef.current = {};
    };
  }, [renderedPages, isDoubleSide]);

  // Update current page based on scroll position
  useEffect(() => {
    const container = containerRef.current;
    if (!container || renderedPages.length === 0) return;

    const handleScroll = () => {
      const scrollTop = container.scrollTop;
      const containerHeight = container.clientHeight;
      const scrollPosition = scrollTop + containerHeight / 2;

      // Find which page is currently in view
      let currentPage = 1;
      let accumulatedHeight = 0;

      if (isDoubleSide) {
        // In double-side mode, pages are grouped in pairs
        for (let i = 0; i < renderedPages.length; i += 2) {
          const leftPage = renderedPages[i];
          const rightPage = renderedPages[i + 1];
          
          // Use the height of the taller page (or left if only one)
          const pageHeight = leftPage.viewport.height;
          accumulatedHeight += pageHeight + 16; // 16px for margin

          if (scrollPosition <= accumulatedHeight) {
            currentPage = i + 1; // Show left page number
            break;
          }
        }
      } else {
        // Single page mode
        for (let i = 0; i < renderedPages.length; i++) {
          const pageHeight = renderedPages[i].viewport.height;
          accumulatedHeight += pageHeight + 16; // 16px for margin

          if (scrollPosition <= accumulatedHeight) {
            currentPage = i + 1;
            break;
          }
        }
      }

      if (currentPage !== pageNum) {
        setPageNum(currentPage);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [renderedPages, pageNum, isDoubleSide]);

  const goToPrevPage = () => {
    if (pageNum > 1) {
      const newPageNum = pageNum - 1;
      setPageNum(newPageNum);
      scrollToPage(newPageNum);
    }
  };

  const goToNextPage = () => {
    if (pageNum < numPages) {
      const newPageNum = pageNum + 1;
      setPageNum(newPageNum);
      scrollToPage(newPageNum);
    }
  };

  const scrollToPage = (targetPage) => {
    const container = containerRef.current;
    if (!container || renderedPages.length === 0) return;

    let accumulatedHeight = 0;
    
    if (isDoubleSide) {
      // In double-side mode, find the pair containing the target page
      const pairIndex = Math.floor((targetPage - 1) / 2) * 2;
      for (let i = 0; i < pairIndex; i += 2) {
        const leftPage = renderedPages[i];
        accumulatedHeight += leftPage.viewport.height + 16; // 16px margin
      }
    } else {
      // Single page mode
      for (let i = 0; i < targetPage - 1; i++) {
        accumulatedHeight += renderedPages[i].viewport.height + 16; // 16px margin
      }
    }

    container.scrollTo({
      top: accumulatedHeight,
      behavior: 'smooth'
    });
  };

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const rotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (viewerRef.current) {
        if (viewerRef.current.requestFullscreen) {
          viewerRef.current.requestFullscreen();
        } else if (viewerRef.current.webkitRequestFullscreen) {
          viewerRef.current.webkitRequestFullscreen();
        } else if (viewerRef.current.msRequestFullscreen) {
          viewerRef.current.msRequestFullscreen();
        }
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  const handleFullscreenChange = () => {
    const isCurrentlyFullscreen = !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement
    );
    setIsFullscreen(isCurrentlyFullscreen);
  };

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const toggleDoubleSide = () => {
    setIsDoubleSide(prev => !prev);
  };


  return (
    <div ref={viewerRef} className="fixed inset-0 bg-gray-900 z-50 flex flex-col">
      {/* Top Toolbar */}
      <div className="bg-gray-800 text-white border-b border-gray-700">
        {/* Mobile Toolbar - Compact */}
        <div className="md:hidden px-2 py-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <h2 className="text-xs font-semibold truncate">
                {title || 'PDF Viewer'}
              </h2>
              {numPages > 0 && (
                <span className="text-xs text-gray-400 whitespace-nowrap">
                  ({numPages})
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-700 rounded flex-shrink-0"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center justify-between gap-1">
            {/* Page Navigation */}
            <div className="flex items-center space-x-1">
              <button
                onClick={goToPrevPage}
                disabled={pageNum <= 1}
                className="p-1.5 hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                title="Previous"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs px-2 min-w-[60px] text-center">
                {pageNum}/{numPages}
              </span>
              <button
                onClick={goToNextPage}
                disabled={pageNum >= numPages}
                className="p-1.5 hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                title="Next"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Zoom Controls */}
            <div className="flex items-center space-x-1">
              <button
                onClick={zoomOut}
                className="p-1.5 hover:bg-gray-700 rounded"
                title="Zoom Out"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-xs px-1 min-w-[45px] text-center">
                {Math.round(scale * 100)}%
              </span>
              <button
                onClick={zoomIn}
                className="p-1.5 hover:bg-gray-700 rounded"
                title="Zoom In"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>

            {/* Rotate */}
            <button
              onClick={rotate}
              className="p-1.5 hover:bg-gray-700 rounded"
              title="Rotate"
            >
              <RotateCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Desktop Toolbar */}
        <div className="hidden md:flex px-4 py-3 items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            {/* Document Title */}
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <h2 className="text-sm font-semibold truncate">
                {title || 'PDF Viewer'}
              </h2>
              {numPages > 0 && (
                <span className="text-xs text-gray-400">
                  ({numPages} pages)
                </span>
              )}
            </div>
            
            {/* Page Navigation */}
            <div className="flex items-center space-x-2">
              <button
                onClick={goToPrevPage}
                disabled={pageNum <= 1}
                className="p-2 hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                title="Previous Page"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm px-3 min-w-[80px] text-center">
                {pageNum} / {numPages}
              </span>
              <button
                onClick={goToNextPage}
                disabled={pageNum >= numPages}
                className="p-2 hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                title="Next Page"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Zoom Controls */}
            <div className="flex items-center space-x-2">
              <button
                onClick={zoomOut}
                className="p-2 hover:bg-gray-700 rounded"
                title="Zoom Out"
              >
                <ZoomOut className="w-5 h-5" />
              </button>
              <span className="text-sm px-2 min-w-[60px] text-center">
                {Math.round(scale * 100)}%
              </span>
              <button
                onClick={zoomIn}
                className="p-2 hover:bg-gray-700 rounded"
                title="Zoom In"
              >
                <ZoomIn className="w-5 h-5" />
              </button>
            </div>

            {/* Rotate */}
            <button
              onClick={rotate}
              className="p-2 hover:bg-gray-700 rounded"
              title="Rotate"
            >
              <RotateCw className="w-5 h-5" />
            </button>

            {/* Double Side Reading */}
            <button
              onClick={toggleDoubleSide}
              className={`p-2 rounded ${isDoubleSide ? 'bg-gray-700' : 'hover:bg-gray-700'}`}
              title="Double Side Reading (2 pages side by side)"
            >
              <Columns className="w-5 h-5" />
            </button>

            {/* Fullscreen / Minimize */}
            {!isFullscreen ? (
              <button
                onClick={toggleFullscreen}
                className="p-2 hover:bg-gray-700 rounded"
                title="Enter Fullscreen"
              >
                <Maximize2 className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={toggleFullscreen}
                className="p-2 hover:bg-gray-700 rounded"
                title="Exit Fullscreen (Minimize)"
              >
                <Minimize2 className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded ml-4"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* PDF Canvas Container */}
      <div 
        ref={containerRef}
        className="flex-1 overflow-y-auto overflow-x-auto bg-gray-700 flex items-start justify-center p-2 md:p-4 relative"
        style={{ minHeight: 0 }}
      >
        {loading && (
          <div className="text-white text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
            <p>Loading PDF...</p>
            <p className="text-sm text-gray-400 mt-2">Reading document without downloading</p>
          </div>
        )}
        
        {error && (
          <div className="text-white text-center max-w-md">
            <p className="text-red-400 mb-4">Error: {error}</p>
            <div className="space-y-2">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded mr-2"
              >
                Retry
              </button>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded inline-block"
              >
                Open in New Tab
              </a>
            </div>
          </div>
        )}

        {!loading && !error && renderedPages.length > 0 && (
          <div className={`w-full ${isDoubleSide ? 'max-w-7xl' : 'max-w-4xl'} space-y-2 md:space-y-4`}>
            {isDoubleSide ? (
              // Double side mode: 2 pages side by side
              <div className="flex flex-wrap justify-center gap-2 md:gap-4">
                {renderedPages.map(({ viewport, pageNum: pNum }, index) => {
                  // Group pages in pairs
                  if (index % 2 === 0) {
                    const leftPage = renderedPages[index];
                    const rightPage = renderedPages[index + 1];
                    
                    return (
                      <div key={`pair-${index}`} className="flex flex-col md:flex-row gap-2 md:gap-4 justify-center w-full md:w-auto">
                        {/* Left page */}
                        <div
                          className="bg-white shadow-2xl mx-auto md:mx-0"
                          style={{ 
                            width: isMobile ? '100%' : leftPage.viewport.width,
                            maxWidth: isMobile ? '100%' : leftPage.viewport.width
                          }}
                        >
                          <canvas
                            ref={(el) => {
                              if (el) canvasRefs.current[leftPage.pageNum] = el;
                            }}
                            className="w-full h-auto block"
                            style={{ maxWidth: '100%' }}
                          />
                        </div>
                        {/* Right page (if exists) */}
                        {rightPage && (
                          <div
                            className="bg-white shadow-2xl mx-auto md:mx-0"
                            style={{ 
                              width: isMobile ? '100%' : rightPage.viewport.width,
                              maxWidth: isMobile ? '100%' : rightPage.viewport.width
                            }}
                          >
                            <canvas
                              ref={(el) => {
                                if (el) canvasRefs.current[rightPage.pageNum] = el;
                              }}
                              className="w-full h-auto block"
                              style={{ maxWidth: '100%' }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            ) : (
              // Single page mode: pages stacked vertically
              renderedPages.map(({ viewport, pageNum: pNum }) => (
                <div
                  key={pNum}
                  className="bg-white shadow-2xl mx-auto"
                  style={{ 
                    width: isMobile ? '100%' : viewport.width,
                    maxWidth: isMobile ? '100%' : viewport.width
                  }}
                >
                  <canvas
                    ref={(el) => {
                      if (el) canvasRefs.current[pNum] = el;
                    }}
                    className="w-full h-auto block"
                    style={{ maxWidth: '100%' }}
                  />
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InbuiltPDFViewer;

